// ISDP CUSTOM: Zero-result AI suggestions query logic for the search modal.
// Extracted verbatim from SearchModal. Must be used INSIDE a
// SearchContextProvider so searchApiRef.query() validates candidates against the
// live index. Fires once per unique zero-result settled term; each candidate is
// validated against the live search API — dead-end suggestions are never shown.
import { useEffect, useRef, useState } from 'react';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { searchApiRef } from '@backstage/plugin-search-react';

export function useZeroResultSuggestions(term: string, hasResults: boolean | null) {
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const searchApi = useApi(searchApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [suggestions, setSuggestions] = useState<string[]>([]);
  // Guard: track the last zero-result term we fetched for — avoids duplicate calls.
  const lastFetchedTerm = useRef('');

  useEffect(() => {
    if (hasResults !== false) {
      // Results found, or term cleared — wipe stale chips.
      setSuggestions([]);
      return undefined;
    }
    const trimmed = term.trim();
    if (!trimmed || trimmed === lastFetchedTerm.current) return undefined;

    lastFetchedTerm.current = trimmed;
    setSuggestions([]); // clear previous chips while fetching

    let cancelled = false;

    (async () => {
      try {
        const res = await fetchApi.fetch(`${backendUrl}/api/ai/search-suggest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ term: trimmed }),
        });
        if (!res.ok || cancelled) return;
        const data: unknown = await res.json();
        const candidates: string[] =
          data !== null &&
          typeof data === 'object' &&
          'suggestions' in data &&
          Array.isArray((data as { suggestions: unknown }).suggestions)
            ? ((data as { suggestions: string[] }).suggestions)
            : [];
        if (!candidates.length || cancelled) return;

        // Validate: only keep candidates that resolve to ≥1 real search result.
        const validated = await Promise.all(
          candidates.map(async (s: string) => {
            try {
              const r = await searchApi.query({ term: s });
              return (r?.results?.length ?? 0) > 0 ? s : null;
            } catch {
              return null;
            }
          }),
        );
        if (cancelled) return;
        setSuggestions(validated.filter((s): s is string => s !== null));
      } catch {
        // Fail-open: network/auth errors → no chips shown.
      }
    })();

    return () => { cancelled = true; };
  // hasResults is the stable settled-state sentinel; term changes reset the guard.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, hasResults]);

  // When the term is cleared, also reset the last-fetched guard.
  useEffect(() => {
    if (!term) {
      lastFetchedTerm.current = '';
      setSuggestions([]);
    }
  }, [term]);

  return suggestions;
}
